<script lang="ts">
  /**
   * F6 — chores that pay, and a ledger the boys can read.
   *
   * This is the only screen in the estate with users who turn up unprompted. An
   * eight-year-old checking a balance is more reliable engagement than either
   * adult, which means two things about how it is built:
   *
   *  1. **It is designed for a child to read.** Bigger type than the rest of the
   *     portal, one idea per row, and every state written out as a sentence a
   *     Grade 3 reader can get through. No abbreviations, no jargon, no
   *     "pending".
   *  2. **It has to be right in front of witnesses.** The whole promise is "so
   *     it is never a dispute", so the balance and the rows that produced it are
   *     on the same screen. A total you cannot audit is a total you argue about.
   *
   * The claim/confirm split is the feature. A boy taps *I did it* on the kitchen
   * tablet as `display`; a parent turns that into money from their phone as
   * `partner`. The server enforces it — see chores.ts — and this screen only
   * renders the half the reader is allowed to use.
   */
  import { Icon, Panel, zar, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type Chore = { id: string; title: string; detail: string; who: string; value: number; active: boolean };
  type Claim = {
    id: string;
    choreId: string | null;
    choreTitle: string;
    who: string;
    value: number;
    status: string;
    claimedAt: string | null;
    confirmedAt: string | null;
    hasPhoto: boolean;
    reason: string | null;
  };
  type Entry = { id: string; who: string; kind: "earned" | "paid"; amount: number; note: string | null; at: string | null };
  type Board = {
    children: string[];
    balances: Record<string, number>;
    chores: Chore[];
    claims: Claim[];
    ledger: Entry[];
  };

  /** True when the reader may confirm work and hand over money. */
  let { canConfirm = false }: { canConfirm?: boolean } = $props();

  let board = $state<Board | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let busy = $state<string | null>(null);
  /** Kept separate from `error`: a failed photo must not blank the board. */
  let notice = $state<string | null>(null);

  async function load() {
    loading = true;
    error = null;
    try {
      board = await api.get<Board>("/chores");
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not reach Homestead. This is not an empty board — try again.";
    } finally {
      loading = false;
    }
  }
  void load();

  const NAME: Record<string, string> = { liam: "Liam", eben: "Eben" };
  const name = (who: string) => NAME[who] ?? who;

  const children = $derived(board?.children ?? []);
  const waiting = $derived((board?.claims ?? []).filter((c) => c.status === "waiting"));

  /**
   * A chore is off the *still to do* list once it has been claimed today, even
   * if a parent has not looked at it yet. Otherwise the tablet invites the same
   * job to be claimed a second time an hour later, and the server's dedupe
   * silently swallows it — a button that does nothing is worse than no button.
   */
  const claimedToday = $derived(
    new Set(
      (board?.claims ?? [])
        .filter((c) => c.claimedAt?.slice(0, 10) === new Date().toISOString().slice(0, 10))
        .map((c) => c.choreId)
        .filter(Boolean) as string[],
    ),
  );

  // A plain function, not a `$derived` returning one: a derived only tracks what
  // it reads while it is being evaluated, and an arrow's body is not read then —
  // so a derived factory would compute once and never update again.
  const todo = (who: string) => (board?.chores ?? []).filter((c) => c.who === who && !claimedToday.has(c.id));
  const listed = (who: string) => (board?.chores ?? []).some((c) => c.who === who);

  /**
   * The screen in one sentence, which is the home-surface rule. Written for the
   * reader standing in front of it, so it leads with the balances — that is what
   * anyone walking past is actually here for.
   */
  const sentence = $derived.by(() => {
    if (!board) return "";
    const money = children.map((w) => `${name(w)} has ${zar(board?.balances[w] ?? 0)}`).join(". ");
    const n = waiting.length;
    const tail =
      n === 0
        ? "Nothing is waiting for a grown-up."
        : n === 1
          ? "One job is waiting for a grown-up."
          : `${n} jobs are waiting for a grown-up.`;
    return `${money}. ${tail}`;
  });

  async function claim(c: Chore) {
    busy = c.id;
    notice = null;
    try {
      await api.post<{ id: string }>(`/chores/${c.id}/claim`);
      await load();
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "That did not go through. Try once more.";
    } finally {
      busy = null;
    }
  }

  async function decide(c: Claim, status: "confirmed" | "returned") {
    let reason: string | null = null;
    if (status === "returned") {
      reason = window.prompt(`What still needs doing on "${c.choreTitle}"?`);
      // The server refuses a return with no reason, and it is right to: work sent
      // back without a word teaches a child the system is arbitrary.
      if (!reason?.trim()) return;
    }
    busy = c.id;
    notice = null;
    try {
      await api.patch(`/claims/${c.id}`, { status, reason });
      await load();
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "Could not save that.";
    } finally {
      busy = null;
    }
  }

  /**
   * Photograph to prove.
   *
   * The photo is evidence, never a gate — a claim stands without one, and the
   * bucket being unreachable must not stop a boy saying he swept the stoep. So
   * the failure is a notice beside the row rather than an error that replaces
   * the board.
   */
  let photoFor = $state<string | null>(null);
  let fileInput = $state<HTMLInputElement | null>(null);

  function pickPhoto(claimId: string) {
    photoFor = claimId;
    fileInput?.click();
  }

  async function uploadPhoto(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    const claimId = photoFor;
    input.value = "";
    if (!file || !claimId) return;

    busy = claimId;
    notice = null;
    try {
      const { url, contentType } = await api.post<{ url: string; contentType: string }>(`/claims/${claimId}/photo`);
      const put = await fetch(url, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
      if (!put.ok) throw new ApiError(put.status, "The photo did not upload.");
      await load();
    } catch (e) {
      notice =
        e instanceof ApiError
          ? `The claim is saved — only the photo failed. ${e.message}`
          : "The claim is saved — only the photo failed.";
    } finally {
      busy = null;
      photoFor = null;
    }
  }

  async function showPhoto(claimId: string) {
    try {
      const { url } = await api.get<{ url: string }>(`/claims/${claimId}/photo`);
      window.open(url, "_blank", "noreferrer");
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "Could not open that photo.";
    }
  }

  // ── adding a chore, and paying one out ────────────────────────────────────
  let adding = $state(false);
  let draft = $state({ title: "", detail: "", who: "liam", value: "" });
  let paying = $state<string | null>(null);
  let payAmount = $state("");
  let payNote = $state("");

  async function addChore() {
    if (!draft.title.trim() || !draft.value.trim()) {
      notice = "A chore needs a name and what it pays.";
      return;
    }
    busy = "new";
    notice = null;
    try {
      await api.post("/chores", {
        title: draft.title.trim(),
        detail: draft.detail.trim(),
        who: draft.who,
        value: Number(draft.value),
      });
      draft = { title: "", detail: "", who: draft.who, value: "" };
      adding = false;
      await load();
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "Could not add that.";
    } finally {
      busy = null;
    }
  }

  async function payOut(who: string) {
    if (!payAmount.trim()) {
      notice = "Say how much was handed over.";
      return;
    }
    busy = `pay-${who}`;
    notice = null;
    try {
      await api.post("/ledger", { who, amount: Number(payAmount), note: payNote.trim() || null });
      payAmount = "";
      payNote = "";
      paying = null;
      await load();
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "Could not record that.";
    } finally {
      busy = null;
    }
  }
</script>

<!-- `sd-panel` is what the container query in tokens.css actually targets (see
     DEVIATION D1 there): below 560px of container width the rows lose their fixed
     height and become cards, rather than scrolling sideways on the tablet. -->
<div class="wrap sd-panel">
  <header class="top">
    <h2 class="h1">Pocket money</h2>
    <p class="lede">{sentence || "Reading the board…"}</p>
  </header>

  {#if notice}
    <p class="notice" role="status"><Icon name="alert" size={13} /> <span>{notice}</span></p>
  {/if}

  <Panel
    {loading}
    {error}
    isEmpty={board !== null && board.chores.length === 0 && board.ledger.length === 0}
    onretry={load}
    variant="card"
    rows={2}
  >
    {#snippet empty()}
      <p class="empt">
        <strong>No chores yet.</strong> Add the first one and say what it pays — Liam and Eben each get
        their own list, and a job with no value beside it is a request rather than a chore.
      </p>
      {#if canConfirm}
        <button class="btn-primary" onclick={() => (adding = true)}>Add the first chore</button>
      {:else}
        <p class="empt">Ask Mum or Dad to put the first one up.</p>
      {/if}
    {/snippet}

    <div class="boards">
      {#each children as who (who)}
        {@const bal = board?.balances[who] ?? 0}
        {@const mine = waiting.filter((c) => c.who === who)}
        <section class="board">
          <div class="board__head">
            <h3 class="who">{name(who)}</h3>
            <!-- The one number. Plain ink, never a status colour: money that goes
                 blue when it is "good" teaches that a small balance is bad, and
                 the estate's rule is that no figure carries meaning by hue. -->
            <div class="fig">{zar(bal)}</div>
            <p class="board__sub">
              {bal === 0 ? "Nothing saved up yet." : "Saved up, not yet paid out."}
            </p>
          </div>

          {#if mine.length > 0}
            <h4 class="lbl">Waiting for a grown-up</h4>
            {#each mine as c (c.id)}
              <div class="row row--attn">
                <span class="mark mark--warn" aria-hidden="true"><Icon name="clock" size={16} /></span>
                <div class="grow">
                  <div class="row__t">{c.choreTitle}</div>
                  <!-- Glyph and word and colour, all three. In greyscale the
                       sentence still says exactly which state this is. -->
                  <div class="state state--warn">
                    <Icon name="clock" size={12} /> Waiting to be checked
                    <span class="dotsep">·</span>
                    {c.hasPhoto ? "photo taken" : "no photo"}
                  </div>
                </div>
                <div class="money">{zar(c.value)}</div>
                <div class="acts">
                  {#if c.hasPhoto}
                    <button class="lnk" onclick={() => showPhoto(c.id)}>See the photo</button>
                  {:else}
                    <button class="lnk" disabled={busy === c.id} onclick={() => pickPhoto(c.id)}>
                      <Icon name="camera" size={13} /> Add a photo
                    </button>
                  {/if}
                  {#if canConfirm}
                    <button class="lnk" disabled={busy === c.id} onclick={() => decide(c, "returned")}>
                      Send it back
                    </button>
                    <button class="btn-primary" disabled={busy === c.id} onclick={() => decide(c, "confirmed")}>
                      Yes, pay it
                    </button>
                  {/if}
                </div>
              </div>
            {/each}
          {/if}

          <h4 class="lbl">Still to do</h4>
          {#each todo(who) as c (c.id)}
            <div class="row">
              <span class="mark" aria-hidden="true"><Icon name="chore" size={16} /></span>
              <div class="grow">
                <div class="row__t">{c.title}</div>
                {#if c.detail}<div class="row__d">{c.detail}</div>{/if}
                <div class="state"><Icon name="chore" size={12} /> Not done yet</div>
              </div>
              <div class="money">{zar(c.value)}</div>
              <div class="acts">
                <button class="btn-primary big-tap" disabled={busy === c.id} onclick={() => claim(c)}>
                  {busy === c.id ? "Sending…" : "I did it"}
                </button>
              </div>
            </div>
          {:else}
            <p class="quiet">
              {#if listed(who)}
                Everything on {name(who)}'s list has been claimed today.
              {:else}
                {name(who)} has no chores on his list yet. A grown-up puts them up.
              {/if}
            </p>
          {/each}

          <h4 class="lbl">The ledger</h4>
          {#each (board?.ledger ?? []).filter((e) => e.who === who).slice(0, 8) as e (e.id)}
            <div class="led">
              {#if e.kind === "earned"}
                <span class="state state--ok"><Icon name="check" size={12} /> Paid in</span>
              {:else}
                <span class="state"><Icon name="logout" size={12} /> Handed over</span>
              {/if}
              <span class="led__n">{e.note}</span>
              <span class="led__d">{dateZA(e.at)}</span>
              <span class="money">{e.kind === "paid" ? "−" : ""}{zar(e.amount)}</span>
            </div>
          {:else}
            <p class="quiet">Nothing has been paid in yet. The first confirmed chore starts this list.</p>
          {/each}

          {#if canConfirm}
            {#if paying === who}
              <div class="edit">
                <label class="f"><span>How much was handed over (R)</span><input type="number" step="1" bind:value={payAmount} /></label>
                <label class="f f--wide"><span>What for</span><input bind:value={payNote} placeholder="Cash, Saturday" /></label>
                <div class="acts">
                  <button class="lnk" onclick={() => (paying = null)}>Cancel</button>
                  <button class="btn-primary" disabled={busy === `pay-${who}`} onclick={() => payOut(who)}>
                    Record it
                  </button>
                </div>
              </div>
            {:else}
              <div class="acts acts--foot">
                <button class="lnk" onclick={() => (paying = who)}>Record money handed over</button>
              </div>
            {/if}
          {/if}
        </section>
      {/each}
    </div>

    {#if canConfirm}
      {#if adding}
        <div class="edit">
          <label class="f f--wide"><span>What needs doing</span><input bind:value={draft.title} placeholder="Feed the dogs" /></label>
          <label class="f f--wide"><span>Anything he should know</span><input bind:value={draft.detail} /></label>
          <label class="f">
            <span>Whose list</span>
            <select bind:value={draft.who}>
              {#each children as w (w)}<option value={w}>{name(w)}</option>{/each}
            </select>
          </label>
          <label class="f"><span>What it pays (R)</span><input type="number" step="1" bind:value={draft.value} /></label>
          <div class="acts">
            <button class="lnk" onclick={() => (adding = false)} disabled={busy === "new"}>Cancel</button>
            <button class="btn-primary" onclick={addChore} disabled={busy === "new"}>Add it</button>
          </div>
        </div>
      {:else}
        <div class="acts acts--foot">
          <button class="btn-primary" onclick={() => (adding = true)}>Add a chore</button>
        </div>
      {/if}
    {/if}
  </Panel>

  <!-- `capture` opens the camera straight away on the tablet rather than a file
       browser, which is the difference between proof happening and not. -->
  <input
    class="hidden-file"
    type="file"
    accept="image/*"
    capture="environment"
    bind:this={fileInput}
    onchange={uploadPhoto}
    tabindex="-1"
    aria-hidden="true"
  />
</div>

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: var(--gap-section);
  }
  .top { display: flex; flex-direction: column; gap: var(--sp-2); }
  .h1 {
    margin: 0;
    font-size: var(--fs-h2);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--tx);
  }
  /* The one sentence that states the whole screen. Body size, not a caption:
     this is the line a household actually reads. */
  .lede { margin: 0; font-size: var(--fs-body); color: var(--tx2); }

  .notice {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    margin: 0;
    padding: var(--sp-3) var(--sp-4);
    border-left: 3px solid var(--warn);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx);
  }

  .empt { margin: 0 0 var(--sp-3); }

  .boards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: var(--sp-5);
  }
  .board {
    display: flex;
    flex-direction: column;
    padding: var(--sp-4) var(--pad-x) var(--sp-5);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .board__head { padding-bottom: var(--sp-4); border-bottom: 1px solid var(--line); }
  .who {
    margin: 0;
    font-size: var(--fs-h3);
    font-weight: 700;
    color: var(--tx);
  }
  /* .fig is the design system's figure treatment — serif and 30px on a home
     surface, mono and 22px on an operator one. Never a hard-coded size. */
  .fig { margin-top: var(--sp-1); }
  .board__sub { margin: var(--sp-1) 0 0; font-size: var(--fs-small); color: var(--mut); }

  .lbl {
    margin: var(--sp-4) 0 var(--sp-2);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }

  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--sp-3);
    min-height: var(--row-h);
    padding: var(--sp-3) 0;
    border-bottom: 1px solid var(--line);
  }
  /* A left edge as well as the colour, so the state survives greyscale and
     survives a reader who cannot separate amber from grey. */
  .row--attn { border-left: 3px solid var(--warn); padding-left: var(--sp-3); }
  .mark { display: grid; place-items: center; color: var(--mut); flex-shrink: 0; }
  .mark--warn { color: var(--warn); }
  .grow { flex: 1; min-width: 8rem; }
  /* Deliberately a step larger than the rest of the portal: an eight-year-old
     reads this across a kitchen at an angle. */
  .row__t { font-size: var(--fs-lead); font-weight: 700; color: var(--tx); line-height: 1.3; }
  .row__d { margin-top: 2px; font-size: var(--fs-small); color: var(--mut); }

  .state {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-1);
    margin-top: var(--sp-1);
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--mut);
  }
  .state--warn { color: var(--warn); }
  /* Blue for good, amber for attention. Never green, and never on money. */
  .state--ok { color: var(--ok); }
  .dotsep { color: var(--mut); font-weight: 400; }

  .money {
    font-family: var(--font-figure);
    font-size: var(--fs-lead);
    font-weight: 700;
    color: var(--tx);
    white-space: nowrap;
  }

  .acts { display: flex; align-items: center; gap: var(--sp-3); flex-wrap: wrap; }
  .acts--foot { justify-content: flex-end; margin-top: var(--sp-4); }
  /* The tap target a child uses, on a tablet, without aiming. */
  .big-tap { padding: 10px 18px; font-size: var(--fs-small); }
  .lnk {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-1);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .lnk:hover { color: var(--acc); }

  .led {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    padding: var(--sp-2) 0;
    border-bottom: 1px solid var(--line);
    font-size: var(--fs-small);
  }
  .led__n { flex: 1; min-width: 0; color: var(--tx2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .led__d { color: var(--mut); white-space: nowrap; }
  .led .money { font-size: var(--fs-body); }

  .quiet { margin: var(--sp-2) 0; font-size: var(--fs-small); color: var(--mut); }

  .edit {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--sp-3);
    margin-top: var(--sp-4);
    padding: var(--sp-4) var(--pad-x);
    border-radius: var(--r-control);
    background: var(--s2);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .edit .acts { grid-column: 1 / -1; justify-content: flex-end; }
  .f { display: flex; flex-direction: column; gap: var(--sp-1); font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .f--wide { grid-column: 1 / -1; }
  .f input,
  .f select {
    min-height: 38px;
    padding: var(--sp-2) var(--sp-3);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
  }

  /* Kept in the layout but out of sight: `display: none` on a file input makes
     `.click()` a no-op in some browsers, which is a photo button that silently
     does nothing. */
  .hidden-file {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
</style>
